import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import * as realtime from '../../../features/realtime/realtime.slice';
import { RealtimeMessageType } from '../../../features/realtime/realtime.types';
import {
  type ScreenshareEventData,
  ScreenshareRequestPayload,
} from '../screenshareState.types';
import {
  getCurrentRequestedMember,
  getScreenshareState,
} from '../../../features/screenshare/screenshare.selectors';
import { setCurrentRequestedMember } from '../../../features/screenshare/screenshare.slice';
import { ScreenshareState } from '../../../features/screenshare/screenshare.types';
import { closeModal } from '../../../features/modals/modals.slice';
import { ModalId } from '../../../features/modals/modals.type';
import { useCallParticipants } from '../../useCallParticipants';
import { TrackedEvents } from '../../../features/tracking/track';
import { useTrack } from '../../useTrack';
import { toast } from '@doxyme/ui/components/foundations';
import { useIntl } from '@doxyme/locale';
import { session } from '../../../webrtc/session';

export const useCancelScreenshareRequest = (): ((
  eventData?: ScreenshareEventData,
) => Promise<void>) => {
  const track = useTrack();
  const dispatch = useAppDispatch();
  const currentRequestedMemberId = useAppSelector(getCurrentRequestedMember);
  const screenshareState = useAppSelector(getScreenshareState);
  const { callParticipantsMap } = useCallParticipants();
  const { formatMessage } = useIntl();

  const cancelScreenshareRequest = async (): Promise<void> => {
    if (currentRequestedMemberId === undefined) {
      throw new Error(
        'Screenshare Canceled but currentRequestedMemberId is undefined',
      );
    }
    await dispatch(
      realtime.sendMessage({
        id: currentRequestedMemberId,
        message: {
          messageType: RealtimeMessageType.ScreenshareRequest,
          payload: { eventName: ScreenshareRequestPayload.Cancel },
        },
      }),
    );

    dispatch(setCurrentRequestedMember(undefined));
    void track(TrackedEvents.ButtonClicked, {
      button_action: 'Screenshare Request Canceled',
    });
  };

  return async (eventData?: ScreenshareEventData): Promise<void> => {
    // This is the case provider cancelling the request while waiting for the patient to accept
    if (screenshareState === ScreenshareState.WaitingForRequestResponse) {
      await cancelScreenshareRequest();
    }

    // This is the patient receiving the cancellation event from the provider
    if (
      (screenshareState === ScreenshareState.RequestReceived ||
        screenshareState === ScreenshareState.ChoosingWindowToShare) &&
      eventData?.realtimeMemberId !== undefined
    ) {
      const provider = callParticipantsMap[eventData.realtimeMemberId];

      toast.success({
        title: formatMessage(
          { id: 'screenShare.providerCancelledRequest' },
          { name: provider?.username },
        ),
      });
    }
    dispatch(setCurrentRequestedMember(undefined));
    dispatch(closeModal(ModalId.Screenshare));
    await session.stopScreenShare();
  };
};
